import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_ROWS_PER_PAGE } from '../../../constants/constants';
import { TableColumnInterface } from '../../../interfaces/tableColumn.interface';
import useGetTokenSessions from '../../../apiHooks/queries/authTokens/useGetTokenSessions';
import { useDeleteSessionToken } from '../../../apiHooks/mutations/authTokens/tokenMutations';
import TokensTable from '../../../components/tables/tokensTable/tokensTable';
import ConfirmDialog from '../../../dialogs/confirmDialog';
import DateTimeCell from '../../../partials/dateTimeCell';
import IdTooltipCell from '../../../partials/idTooltipCell';

const columns: TableColumnInterface[] = [
  { field: 'id', headerName: 'id', cellRenderer: (params: string) => <IdTooltipCell params={params} /> },
  { field: 'userId', headerName: 'userId' },
  { field: 'organizationId', headerName: 'organizationId' },
  { field: 'createdAt', headerName: 'createdAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'expiresAt', headerName: 'expiresAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
  { field: 'revokedAt', headerName: 'revokedAt', cellRenderer: (params: string) => <DateTimeCell params={params} /> },
];

const TokenSessionsTab = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTokenId, setSelectedTokenId] = useState<string | number | null>(null);
  const [searchParams] = useSearchParams();
  const { mutateAsync: deleteSessionToken, isLoading: isDeleting } = useDeleteSessionToken();

  const [searchInput, setSearchInput] = useState({
    search: searchParams.get('search') || '',
  });

  useEffect(() => {
    setPage(0);
  }, [searchInput.search, rowsPerPage]);

  const handleDeleteClick = (token: any) => {
    setSelectedTokenId(token?.id ?? null);
    setIsDeleteDialogOpen(true);
  };

  const handleOnDelete = async () => {
    if (selectedTokenId == null) return;
    await deleteSessionToken(selectedTokenId);
    setIsDeleteDialogOpen(false);
    setSelectedTokenId(null);
    refetch();
  };

  const {
    data: sessionsData,
    isError,
    isFetching,
    refetch,
    error,
  } = useGetTokenSessions(
    'TOKENS_SESSIONS',
    {
      search: searchInput.search,
      rows: rowsPerPage,
      page: page + 1,
    },
  );

  const is404 = (error as any)?.response?.status === 404;

  if (is404) {
    return (
      <div className="w-full rounded-lg border border-[#EAEAEA] bg-white p-4 text-sm text-primary">
        {t('tokensSessionsUnavailable')}
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        variant="warning"
        title="deleteToken"
        text="deleteTokenConfirm"
        submitButtonText="delete"
        isDisabled={isDeleting}
        onCloseDialog={() => {
          setIsDeleteDialogOpen(false);
          setSelectedTokenId(null);
        }}
        onSubmit={handleOnDelete}
      />
      <TokensTable
        title="tokensSessions"
        columns={columns}
        rows={sessionsData?.data ?? []}
        total={sessionsData?.meta?.total ?? 0}
        page={page}
        rowsPerPage={rowsPerPage}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        isError={isError}
        isFetching={isFetching}
        isDeleting={isDeleting}
        refetch={refetch}
        search={searchInput.search}
        setSearch={(search) => setSearchInput({ search })}
        onDelete={handleDeleteClick}
      />
    </>
  );
};

export default TokenSessionsTab;
